import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button, CopyId, EmptyState, ErrorState } from '@bb/ui';
import { ApiError, type NewSubscriptionGroup, type SubscriptionChannel } from '../api/client.ts';
import { Can } from '../features/access/Can.tsx';

export const CHANNEL_LABEL: Record<SubscriptionChannel, string> = { email: 'Email', push: 'Push', sms: 'SMS', whatsapp: 'WhatsApp' };
const PURPOSE_LABEL = { marketing: 'Marketing', notifications: 'Notifications', subscriptions: 'Subscriptions' } as const;
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/** Audience → Subscription groups (BB-32): topics users opt into per channel; sends check them right before sending. */
export function SubscriptionGroupsPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/audience/subscription-groups' });
  const { api } = useRouteContext({ from: '/env/$environmentId/audience/subscription-groups' });
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'' | 'active' | 'archived'>('active');
  const [channel, setChannel] = useState<'' | SubscriptionChannel>('');
  const groups = useQuery({ queryKey: ['subscription-groups', environmentId, status, channel], queryFn: () => api.listSubscriptionGroups(environmentId, { status, channel }) });
  const [form, setForm] = useState<NewSubscriptionGroup>({ name: '', channel: 'push', purpose: 'notifications', topic: '' });
  const create = useMutation({
    mutationFn: () => api.createSubscriptionGroup(environmentId, { ...form, name: form.name.trim(), topic: form.topic.trim() }),
    onSuccess: () => {
      setForm({ ...form, name: '', topic: '' });
      void queryClient.invalidateQueries({ queryKey: ['subscription-groups', environmentId] });
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (form.name.trim() && form.topic.trim()) create.mutate();
  };

  return (
    <section>
      <h1>Subscription groups</h1>
      <p className="hint">Every send to a group checks the user's state in it right before sending; an unsubscribe stops future sends.</p>
      <div className="filters">
        <label>
          Status{' '}
          <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </label>{' '}
        <label>
          Channel{' '}
          <select aria-label="Filter by channel" value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
            <option value="">All channels</option>
            {Object.entries(CHANNEL_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {groups.isPending ? (
        <p aria-busy="true">Loading groups…</p>
      ) : groups.isError ? (
        <ErrorState title="Groups could not be loaded" message={errorText(groups.error)} onRetry={() => void groups.refetch()} />
      ) : groups.data.length === 0 ? (
        <EmptyState title="No subscription groups" description={status || channel ? 'Nothing matches the filters.' : 'Create the first group below.'} />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Channel</th>
              <th scope="col">Subscribed</th>
              <th scope="col">Unsubscribed</th>
              <th scope="col">API id</th>
            </tr>
          </thead>
          <tbody>
            {groups.data.map((g) => (
              <tr key={g.id}>
                <td>
                  <Link to="/env/$environmentId/audience/subscription-groups/$groupId" params={{ environmentId, groupId: g.id }}>
                    {g.name}
                  </Link>
                  {g.status === 'archived' && <span className="tag tag--neutral"> Archived</span>}
                </td>
                <td>{CHANNEL_LABEL[g.channel]}</td>
                <td>{g.subscribedCount ?? '—'}</td>
                <td>{g.unsubscribedCount ?? '—'}</td>
                <td>
                  <CopyId value={g.apiId} label={`API id of ${g.name}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Can permission="catalog.edit">
        <form className="stack-form" aria-label="Create subscription group" onSubmit={submit}>
          <label>
            Purpose
            <select value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value as NewSubscriptionGroup['purpose'] })}>
              {Object.entries(PURPOSE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Group channel
            <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value as SubscriptionChannel })}>
              {Object.entries(CHANNEL_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Topic
            <input
              value={form.topic}
              onChange={(e) => {
                const topic = e.target.value;
                setForm({ ...form, topic, name: `${PURPOSE_LABEL[form.purpose]} - ${CHANNEL_LABEL[form.channel]} - ${topic}` });
              }}
              placeholder="Price Alerts"
            />
          </label>
          <label>
            Group name
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <Button type="submit" disabled={create.isPending || !form.name.trim() || !form.topic.trim()}>
            Create group
          </Button>
          {create.isError && <span role="alert">{errorText(create.error)}</span>}
        </form>
      </Can>
    </section>
  );
}
